/**
 * THERAPIST NOTES SETTINGS SERVICE
 *
 * Gestión de configuración, carpetas, etiquetas, categorías y métricas de uso.
 * Extraído de therapist-notes.service.ts para mantener responsabilidad única.
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import {
  TherapistNote,
  TherapistNotesUsage,
  TherapistNotesSettings,
  TherapistNotesFolder,
  NOTES_PLAN_LIMITS,
  PREDEFINED_NOTE_CATEGORIES,
  PREDEFINED_NOTE_TAGS,
} from '@/core/types/models/therapist/therapist-notes.model';

const logger = Logger.getLogger('TherapistNotesSettingsService');

const COLLECTIONS = {
  NOTES: 'therapist_notes',
  USAGE: 'therapist_notes_usage',
  FOLDERS: 'therapist_notes_folders',
  SETTINGS: 'therapist_notes_settings',
};

class TherapistNotesSettingsService {

  // ============================================
  // MÉTRICAS DE USO (FACTURACIÓN)
  // ============================================

  async getUsageMetrics(therapistId: string): Promise<TherapistNotesUsage> {
    try {
      const docRef = db.collection(COLLECTIONS.USAGE).doc(therapistId);
      const doc = await docRef.get();

      if (doc.exists) {
        return { id: doc.id, ...doc.data() } as TherapistNotesUsage;
      }

      const now = new Date();
      const periodEnd = new Date(now);
      periodEnd.setMonth(periodEnd.getMonth() + 1);

      const newUsage: TherapistNotesUsage = {
        id: therapistId,
        therapistId,
        totalNotesCount: 0,
        totalChunksCount: 0,
        totalStorageBytes: 0,
        totalCharacters: 0,
        chatQueriesThisMonth: 0,
        plan: 'free_trial',
        storageQuotaBytes: NOTES_PLAN_LIMITS.free_trial.storageBytes,
        queriesQuotaMonth: NOTES_PLAN_LIMITS.free_trial.monthlyQueries,
        periodStart: now.toISOString(),
        periodEnd: periodEnd.toISOString(),
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      };

      await docRef.set(newUsage);
      return newUsage;

    } catch (error: any) {
      logger.error('Failed to get usage metrics', error, { therapistId });
      throw error;
    }
  }

  async updateUsageMetrics(therapistId: string, delta: {
    notesAdded?: number;
    chunksAdded?: number;
    bytesAdded?: number;
    charactersAdded?: number;
    queriesAdded?: number;
  }): Promise<void> {
    try {
      const usage = await this.getUsageMetrics(therapistId);
      const now = new Date().toISOString();

      const updates: Partial<TherapistNotesUsage> = { updatedAt: now };

      if (delta.notesAdded) {
        updates.totalNotesCount = Math.max(0, usage.totalNotesCount + delta.notesAdded);
      }
      if (delta.chunksAdded) {
        updates.totalChunksCount = Math.max(0, usage.totalChunksCount + delta.chunksAdded);
      }
      if (delta.bytesAdded) {
        updates.totalStorageBytes = Math.max(0, usage.totalStorageBytes + delta.bytesAdded);
      }
      if (delta.charactersAdded) {
        updates.totalCharacters = Math.max(0, usage.totalCharacters + delta.charactersAdded);
      }
      if (delta.queriesAdded) {
        updates.chatQueriesThisMonth = usage.chatQueriesThisMonth + delta.queriesAdded;
        updates.lastQueryAt = now;
      }

      await db.collection(COLLECTIONS.USAGE).doc(therapistId).update(updates);

    } catch (error: any) {
      logger.warn('Failed to update usage metrics', { therapistId, error: error.message });
    }
  }

  async checkStorageQuota(therapistId: string, additionalBytes: number): Promise<void> {
    const usage = await this.getUsageMetrics(therapistId);
    const limits = NOTES_PLAN_LIMITS[usage.plan];

    if (usage.totalStorageBytes + additionalBytes > limits.storageBytes) {
      throw new Error(`Has alcanzado el límite de almacenamiento de tu plan (${this.formatBytes(limits.storageBytes)}). Actualiza tu plan para continuar.`);
    }

    if (limits.maxNotesCount > 0 && usage.totalNotesCount >= limits.maxNotesCount) {
      throw new Error(`Has alcanzado el límite de ${limits.maxNotesCount} apuntes de tu plan. Actualiza tu plan para continuar.`);
    }
  }

  async checkQueryQuota(therapistId: string): Promise<boolean> {
    const usage = await this.getUsageMetrics(therapistId);
    const limits = NOTES_PLAN_LIMITS[usage.plan];
    if (limits.monthlyQueries < 0) return true;
    return usage.chatQueriesThisMonth < limits.monthlyQueries;
  }

  async recordQuery(therapistId: string): Promise<void> {
    await this.updateUsageMetrics(therapistId, { queriesAdded: 1 });
  }

  // ============================================
  // ESTADÍSTICAS
  // ============================================

  async getNotesStats(therapistId: string): Promise<{
    totalNotes: number;
    totalChunks: number;
    storageUsed: string;
    queriesUsed: number;
    queriesLimit: number;
    plan: string;
    ontologyNodes: number;
    ontologyConnections: number;
  }> {
    const { therapistNotesOntologyService } = await import('./therapist-notes-ontology.service');
    const usage = await this.getUsageMetrics(therapistId);
    const ontology = await therapistNotesOntologyService.getOntology(therapistId);
    const limits = NOTES_PLAN_LIMITS[usage.plan];

    return {
      totalNotes: usage.totalNotesCount,
      totalChunks: usage.totalChunksCount,
      storageUsed: `${this.formatBytes(usage.totalStorageBytes)} / ${this.formatBytes(limits.storageBytes)}`,
      queriesUsed: usage.chatQueriesThisMonth,
      queriesLimit: limits.monthlyQueries,
      plan: usage.plan,
      ontologyNodes: ontology.totalNodes,
      ontologyConnections: ontology.totalConnections,
    };
  }

  // ============================================
  // ETIQUETAS Y CATEGORÍAS
  // ============================================

  async getNotesSettings(therapistId: string): Promise<TherapistNotesSettings> {
    try {
      const docRef = db.collection(COLLECTIONS.SETTINGS).doc(therapistId);
      const doc = await docRef.get();

      if (doc.exists) {
        return { id: doc.id, ...doc.data() } as TherapistNotesSettings;
      }

      const defaultSettings: TherapistNotesSettings = {
        id: therapistId,
        therapistId,
        customCategories: [],
        customTags: [],
        hiddenCategories: [],
        hiddenTags: [],
        preferences: {
          autoTagEnabled: true,
          showPredefinedTags: true,
          defaultView: 'list',
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await docRef.set(defaultSettings);
      return defaultSettings;

    } catch (error: any) {
      logger.warn('Failed to get notes settings, returning defaults', { therapistId, error: error.message });
      return {
        id: therapistId,
        therapistId,
        customCategories: [],
        customTags: [],
        hiddenCategories: [],
        hiddenTags: [],
        preferences: {
          autoTagEnabled: true,
          showPredefinedTags: true,
          defaultView: 'list' as const,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
  }

  async getAvailableCategories(therapistId: string): Promise<string[]> {
    const settings = await this.getNotesSettings(therapistId);
    const allCategories = [
      ...PREDEFINED_NOTE_CATEGORIES.filter(c => !settings.hiddenCategories.includes(c)),
      ...settings.customCategories,
    ];
    return [...new Set(allCategories)];
  }

  async getAvailableTags(therapistId: string): Promise<string[]> {
    const settings = await this.getNotesSettings(therapistId);
    if (!settings.preferences.showPredefinedTags) {
      return settings.customTags;
    }
    const allTags = [
      ...PREDEFINED_NOTE_TAGS.filter(t => !settings.hiddenTags.includes(t)),
      ...settings.customTags,
    ];
    return [...new Set(allTags)];
  }

  async addCustomCategory(therapistId: string, category: string): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    if (!settings.customCategories.includes(category)) {
      settings.customCategories.push(category);
      settings.updatedAt = new Date().toISOString();
      await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
      logger.info('Custom category added', { therapistId, category });
    }
  }

  async addCustomTag(therapistId: string, tag: string): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    if (!settings.customTags.includes(tag)) {
      settings.customTags.push(tag);
      settings.updatedAt = new Date().toISOString();
      await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
      logger.info('Custom tag added', { therapistId, tag });
    }
  }

  async removeCustomCategory(therapistId: string, category: string): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    settings.customCategories = settings.customCategories.filter(c => c !== category);
    settings.updatedAt = new Date().toISOString();
    await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
  }

  async removeCustomTag(therapistId: string, tag: string): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    settings.customTags = settings.customTags.filter(t => t !== tag);
    settings.updatedAt = new Date().toISOString();
    await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
  }

  async hideTag(therapistId: string, tag: string): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    if (!settings.hiddenTags.includes(tag)) {
      settings.hiddenTags.push(tag);
      settings.updatedAt = new Date().toISOString();
      await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
    }
  }

  async showTag(therapistId: string, tag: string): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    settings.hiddenTags = settings.hiddenTags.filter(t => t !== tag);
    settings.updatedAt = new Date().toISOString();
    await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
  }

  async updatePreferences(
    therapistId: string,
    preferences: Partial<TherapistNotesSettings['preferences']>
  ): Promise<void> {
    const settings = await this.getNotesSettings(therapistId);
    settings.preferences = { ...settings.preferences, ...preferences };
    settings.updatedAt = new Date().toISOString();
    await db.collection(COLLECTIONS.SETTINGS).doc(therapistId).set(settings);
    logger.info('Notes preferences updated', { therapistId, preferences });
  }

  // ============================================
  // CARPETAS
  // ============================================

  async getFolders(therapistId: string): Promise<TherapistNotesFolder[]> {
    try {
      const snapshot = await db.collection(COLLECTIONS.FOLDERS)
        .where('therapistId', '==', therapistId)
        .orderBy('order', 'asc')
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TherapistNotesFolder[];

    } catch (error: any) {
      logger.warn('Failed to get folders, returning empty array', { therapistId, error: error.message });
      return [];
    }
  }

  async createFolder(
    therapistId: string,
    data: Pick<TherapistNotesFolder, 'name' | 'color' | 'icon' | 'parentId'>
  ): Promise<TherapistNotesFolder> {
    try {
      const folders = await this.getFolders(therapistId);
      const now = new Date().toISOString();

      const folder: Omit<TherapistNotesFolder, 'id'> = {
        therapistId,
        name: data.name,
        parentId: data.parentId,
        color: data.color || '#94a3b8',
        icon: data.icon || '',
        order: folders.length,
        notesCount: 0,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await db.collection(COLLECTIONS.FOLDERS).add(folder);
      logger.info('Folder created', { therapistId, folderId: docRef.id, name: data.name });
      return { id: docRef.id, ...folder };

    } catch (error: any) {
      logger.error('Failed to create folder', error, { therapistId });
      throw error;
    }
  }

  async updateFolder(
    folderId: string,
    data: Partial<Pick<TherapistNotesFolder, 'name' | 'color' | 'icon' | 'order'>>
  ): Promise<void> {
    try {
      await db.collection(COLLECTIONS.FOLDERS).doc(folderId).update({
        ...data,
        updatedAt: new Date().toISOString(),
      });
      logger.info('Folder updated', { folderId });
    } catch (error: any) {
      logger.error('Failed to update folder', error, { folderId });
      throw error;
    }
  }

  async deleteFolder(therapistId: string, folderId: string): Promise<void> {
    try {
      const notes = await db.collection(COLLECTIONS.NOTES)
        .where('therapistId', '==', therapistId)
        .where('folderId', '==', folderId)
        .get();

      const batch = db.batch();
      notes.docs.forEach(doc => {
        batch.update(doc.ref, { folderId: null, updatedAt: new Date().toISOString() });
      });
      batch.delete(db.collection(COLLECTIONS.FOLDERS).doc(folderId));
      await batch.commit();

      logger.info('Folder deleted', { therapistId, folderId, notesMoved: notes.size });

    } catch (error: any) {
      logger.error('Failed to delete folder', error, { therapistId, folderId });
      throw error;
    }
  }

  async moveNoteToFolder(noteId: string, folderId: string | null): Promise<void> {
    try {
      const noteDoc = await db.collection(COLLECTIONS.NOTES).doc(noteId).get();
      if (!noteDoc.exists) throw new Error('Note not found');
      const note = { id: noteDoc.id, ...noteDoc.data() } as TherapistNote;

      const oldFolderId = note.folderId;

      await db.collection(COLLECTIONS.NOTES).doc(noteId).update({
        folderId,
        updatedAt: new Date().toISOString(),
      });

      if (oldFolderId) {
        await db.collection(COLLECTIONS.FOLDERS).doc(oldFolderId).update({
          notesCount: (await db.collection(COLLECTIONS.NOTES)
            .where('folderId', '==', oldFolderId).get()).size,
        });
      }

      if (folderId) {
        await db.collection(COLLECTIONS.FOLDERS).doc(folderId).update({
          notesCount: (await db.collection(COLLECTIONS.NOTES)
            .where('folderId', '==', folderId).get()).size,
        });
      }

      logger.info('Note moved to folder', { noteId, folderId });

    } catch (error: any) {
      logger.error('Failed to move note to folder', error, { noteId, folderId });
      throw error;
    }
  }

  // ============================================
  // FILTRADO
  // ============================================

  async getNotesFiltered(
    therapistId: string,
    filters: {
      category?: string;
      tags?: string[];
      folderId?: string | null;
      searchTerm?: string;
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<TherapistNote[]> {
    try {
      let query = db.collection(COLLECTIONS.NOTES)
        .where('therapistId', '==', therapistId) as any;

      if (filters.folderId !== undefined) {
        query = query.where('folderId', '==', filters.folderId);
      }
      if (filters.category) {
        query = query.where('category', '==', filters.category);
      }

      const snapshot = await query.orderBy('createdAt', 'desc').get();
      let notes = snapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data()
      })) as TherapistNote[];

      if (filters.tags && filters.tags.length > 0) {
        notes = notes.filter(note =>
          filters.tags!.some(tag => note.tags.includes(tag))
        );
      }

      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        notes = notes.filter(note =>
          note.title.toLowerCase().includes(term) ||
          note.extractedText.toLowerCase().includes(term) ||
          note.tags.some(t => t.toLowerCase().includes(term))
        );
      }

      if (filters.fromDate) {
        notes = notes.filter(note => note.createdAt >= filters.fromDate!);
      }
      if (filters.toDate) {
        notes = notes.filter(note => note.createdAt <= filters.toDate!);
      }

      return notes;

    } catch (error: any) {
      logger.error('Failed to get filtered notes', error, { therapistId, filters });
      throw error;
    }
  }

  async getTagsSummary(therapistId: string): Promise<Array<{ tag: string; count: number }>> {
    try {
      const { therapistNotesCrudService } = await import('./therapist-notes-crud.service');
      const notes = await therapistNotesCrudService.getNotes(therapistId);
      const tagCounts = new Map<string, number>();

      notes.forEach(note => {
        note.tags.forEach(tag => {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        });
      });

      return Array.from(tagCounts.entries())
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count);

    } catch (error: any) {
      logger.warn('Failed to get tags summary, returning empty array', { therapistId, error: error.message });
      return [];
    }
  }

  async getCategoriesSummary(therapistId: string): Promise<Array<{ category: string; count: number }>> {
    try {
      const { therapistNotesCrudService } = await import('./therapist-notes-crud.service');
      const notes = await therapistNotesCrudService.getNotes(therapistId);
      const categoryCounts = new Map<string, number>();

      notes.forEach(note => {
        if (note.category) {
          categoryCounts.set(note.category, (categoryCounts.get(note.category) || 0) + 1);
        }
      });

      return Array.from(categoryCounts.entries())
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count);

    } catch (error: any) {
      logger.warn('Failed to get categories summary, returning empty array', { therapistId, error: error.message });
      return [];
    }
  }

  // ============================================
  // UTILIDADES
  // ============================================

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const therapistNotesSettingsService = new TherapistNotesSettingsService();
